'use client'

/**
 * Hand-ported from `nivex-design/apps/nivex-web-v3/src/components/TableFilterTabs.tsx` —
 * a generic, domain-free presentational pattern (like `components/brand`, `components/errors`),
 * not a shadcn primitive, so it isn't touched by `scripts/sync-design.mjs`. Re-copy from that
 * source on drift.
 */

import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

export const ALL_FILTER_VALUE = 'all'

/**
 * The search box and result count that sit above a table.
 *
 * Sized so the placeholder never clips, which took three separate fixes — the original bug was
 * a 220px field showing "Search investor or referenc":
 *
 * 1. The field is 260px on desktop. 220px left 176px of text space for a 196px placeholder.
 * 2. Below `@lg` the field and the count stack rather than share a line. Sharing left the field
 *    ~150px once the count took its space, clipping far worse than the original bug.
 * 3. `pl-7` not `pl-8`. The icon is 16px at a 10px offset, so it ends at 26px — 28px of padding
 *    clears it and hands 4px back to the text, which is the difference between fitting and not
 *    at 320px, where even a full-width field has only ~210px to spend.
 *
 * `shrink-0` once the width is fixed, so a long filter row beside it cannot squeeze the text
 * back out. Callers keep placeholders short for the same reason: the tables search a name or a
 * case reference, and "Search name or reference…" fits a 320px card where the older, wordier
 * "Search investor or reference…" did not.
 */
export function TableSearch({
  value,
  onChange,
  placeholder,
  count,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  /** Omit where the toolbar shows no result tally, as on the staff client list. */
  count?: { shown: number; total: number; noun: string }
}) {
  /* Shown only while something is actually filtered out. At rest "10 of 10 cases" repeats what
     the row's own "All (10)" button already says, and it cost 92px of the line — enough that the
     filter buttons had to scroll to fit beside it. Filtered, it says something the buttons
     cannot: they count the unsearched set, so only this reflects the query. */
  const showCount = count != null && count.shown !== count.total

  return (
    <div className="@lg/main:flex-row @lg/main:shrink-0 @lg/main:items-center @lg/main:gap-3 flex flex-col gap-2">
      <div className="@lg/main:w-auto relative w-full">
        <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="@lg/main:w-[260px] @lg/main:shrink-0 w-full pl-7"
        />
      </div>
      {showCount ? (
        <p className="text-muted-foreground whitespace-nowrap text-sm">
          {count!.shown} of {count!.total} {count!.noun}
        </p>
      ) : null}
    </div>
  )
}

export type FilterOption<T extends string> = {
  value: T
  label: string
  /** Omit where the backend's list response carries no per-option count breakdown. */
  count?: number
}

/**
 * The segmented filter row that sits above a staff/admin table.
 *
 * Every one of these dimensions used to be a dropdown, which hid both the available values and
 * how much data sat behind each one — you had to open it to learn that a status existed, and
 * open it again to find out it matched nothing. As buttons the whole distribution is readable
 * without a click, and the counts double as a summary of the table.
 *
 * Built on Radix `Tabs` for the roles and keyboard behaviour (arrow keys move between filters).
 *
 * Callers pass only the values actually present in the data — see `countBy`. A button reading
 * "No Answer (0)" filters to an empty table, which is a dead end dressed up as a control.
 */
export function TableFilterTabs<T extends string>({
  value,
  onValueChange,
  allLabel = 'All',
  allCount,
  options,
  className,
}: {
  value: T | typeof ALL_FILTER_VALUE
  onValueChange: (value: T | typeof ALL_FILTER_VALUE) => void
  allLabel?: string
  allCount: number
  options: FilterOption<T>[]
  className?: string
}) {
  // With nothing to choose between, the row is one dead "All" button. The table's own empty
  // state already explains a table with no rows.
  if (options.length < 2) return null

  return (
    <Tabs
      value={value}
      onValueChange={(v) => onValueChange(v as T | typeof ALL_FILTER_VALUE)}
      className={cn('min-w-0', className)}
    >
      {/* A segmented control cannot wrap — its track is one continuous surface — so at narrow
          widths it scrolls sideways rather than breaking onto a second line.

          `overflow-y-hidden` is not redundant. Setting only `overflow-x` does not leave the
          other axis alone: per spec a `visible` overflow-y computes to `auto` as soon as
          overflow-x is not visible. The track then overflows its own box by the 1px of border,
          which is enough to raise a full vertical scrollbar — 15px wide with stepper arrows on
          Windows — parked at the end of the filter row. */}
      <div className="overflow-x-auto overflow-y-hidden">
        <TabsList>
          <TabsTrigger value={ALL_FILTER_VALUE}>
            {allLabel} ({allCount})
          </TabsTrigger>
          {options.map((option) => (
            <TabsTrigger key={option.value} value={option.value}>
              {option.count == null ? option.label : `${option.label} (${option.count})`}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
    </Tabs>
  )
}

/**
 * Tallies `rows` by `key`, then returns one option per value that is actually present, ordered
 * to match `order` so the buttons keep a stable, meaningful sequence — pipeline order for a
 * case status, for instance — rather than reshuffling as the data changes.
 */
export function countBy<Row, T extends string>(
  rows: Row[],
  key: (row: Row) => T | null | undefined,
  order: readonly T[],
  label: (value: T) => string,
): FilterOption<T>[] {
  const counts = new Map<T, number>()
  for (const row of rows) {
    const value = key(row)
    if (value == null) continue
    counts.set(value, (counts.get(value) ?? 0) + 1)
  }
  return order
    .filter((value) => counts.has(value))
    .map((value) => ({ value, label: label(value), count: counts.get(value)! }))
}
